import React, { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ADMIN_MENU } from "../config/menu";
import { useApp } from "../context/AppContext";
import { useI18n } from "../i18n";
import { useCart } from "../context/CartContext";
import { Avatar } from "../components/ui";
import Icon from "../components/ui/Icon";
import BrandMark from "../components/BrandMark";
import Shell from "./Shell";

export default function AdminLayout() {
  const { organization, organizations, switchOrg, branches, branchId, switchBranch, enabledModules, profile, logout, businessModel, planActive, plan, subscription, moduleLocked } = useApp();
  const { t, tx, lang, setLang, pick } = useI18n();
  const { count: cartItems } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const [orgMenu, setOrgMenu] = useState(false);
  const [userMenu, setUserMenu] = useState(false);

  useEffect(() => {
    setOrgMenu(false);
    setUserMenu(false);
  }, [location.pathname]);

  // เมนูที่ใช้ไม่ได้จะไม่ซ่อน แต่โชว์กุญแจ กดเข้าไปได้ หน้านั้นบอกว่าต้องซื้อ/อัปเกรดแพ็กเกจ
  //   - ยังไม่ซื้อ / หมดอายุ  -> ใช้ชุดฟรี เมนูนอกชุดฟรีล็อก
  //   - โมดูลไม่อยู่ในแพ็กเกจ -> ล็อกเฉพาะเมนูนั้น (ชวนอัปเกรด)
  //   - เจ้าของร้านปิดโมดูลเอง -> ซ่อนตามเดิม
  const groups = useMemo(() => {
    const decorate = (item) => {
      if (item.always) return { ...item, locked: false };
      if (item.module && moduleLocked(item.module)) return { ...item, locked: true };
      if (item.module && !enabledModules.includes(item.module)) return null;
      return { ...item, locked: false };
    };

    return ADMIN_MENU.map((group) => ({ ...group, items: group.items.map(decorate).filter(Boolean) })).filter(
      (group) => group.items.length > 0
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledModules, planActive]);

  // รูปแบบที่ Shell ใช้: แต่ละหมวดมีไอคอนบน rail + รายการเมนูย่อยในแผง
  const railGroups = useMemo(
    () =>
      groups.map((group) => ({
        id: group.group,
        title: pick(group, "group"),
        short: pick(group, "short"),
        railIcon: group.rail,
        items: group.items.map((item) => ({ ...item, label: pick(item, "label") })),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groups, lang]
  );

  const title = useMemo(() => {
    if (location.pathname.startsWith("/admin/cart")) return tx("ตะกร้า", "Cart");
    // หาชื่อหน้าจากเมนูทั้งหมด (รวมโมดูลที่ถูกปิดอยู่ด้วย) เผื่อเข้าหน้าตรงผ่าน URL
    const all = ADMIN_MENU.flatMap((group) => group.items);
    const found = all
      .filter((item) => (item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to)))
      .sort((a, b) => b.to.length - a.to.length)[0];
    return found ? pick(found, "label") : t("app.name");
  }, [location.pathname, pick, t, tx]);

  const closeMenus = () => {
    setOrgMenu(false);
    setUserMenu(false);
  };

  // ร้าน — แต่ละบัญชีผูกกับร้านเดียว ปุ่มสลับร้านโผล่เฉพาะบัญชีที่มีหลายร้าน
  const multiOrg = organizations.length > 1;
  const orgSwitch = (
    <div className="lx-org">
      <button
        type="button"
        className="lx-org__btn"
        onClick={() => multiOrg && setOrgMenu((v) => !v)}
        title={`${pick(businessModel)}${multiOrg ? ` · ${t("nav.switchOrg")}` : ""}`}
      >
        <Icon name={businessModel.icon} />
        <span className="lx-org__name">{organization?.name || "-"}</span>
        {multiOrg ? <span className="lx-org__caret">▾</span> : null}
      </button>
      {orgMenu && multiOrg ? (
        <div className="lx-menu lx-menu--left">
          {organizations.map((org) => (
            <button key={org.id} type="button" className={org.id === organization?.id ? "is-active" : ""} onClick={() => switchOrg(org.id)}>
              {org.name}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );

  // สถานะแพ็กเกจ — เตือนล่วงหน้า 7 วันก่อนหมดอายุ
  const planChip = (
    <Link to="/admin/subscription" className={`lx-plan ${planActive ? "" : "is-warn"}`}>
      <Icon name={planActive ? "badge" : "lock"} />
      <b>{planActive ? pick(plan) : tx("ใช้งานฟรี (จำกัด)", "Free (limited)")}</b>
      <small>
        {planActive
          ? tx(`เหลืออีก ${subscription.daysLeft} วัน`, `${subscription.daysLeft} days left`)
          : tx("ซื้อแพ็กเกจเพื่อปลดล็อกทุกเมนู", "Buy a plan to unlock all menus")}
      </small>
    </Link>
  );

  const branchSelect = (
    <select className="select-crm lx-branch" value={branchId} onChange={(event) => switchBranch(event.target.value)} aria-label={tx("สาขา", "Branch")}>
      <option value="">ทุกสาขา</option>
      {branches.map((item) => (
        <option key={item.id} value={item.id}>
          {pick(item)}
        </option>
      ))}
    </select>
  );

  const langBtn = (
    <button type="button" className="lx-pill" onClick={() => setLang(lang === "th" ? "en" : "th")}>
      {lang === "th" ? "TH" : "EN"}
    </button>
  );

  return (
    <>
      {orgMenu || userMenu ? <div className="lx-scrim" onClick={closeMenus} /> : null}
      <Shell
        groups={railGroups}
        brand={<BrandMark brand={{}} />}
        brandTo="/admin"
        title={title}
        navLabel={tx("หมวดเมนู", "Menu categories")}
        toggleLabel={tx("เปิด/ปิดเมนูย่อย", "Toggle menu panel")}
        closeLabel={tx("ปิด", "Close")}
        lockedHint={tx("ต้องซื้อ/อัปเกรดแพ็กเกจก่อนใช้งาน", "Buy or upgrade your plan to unlock")}
        topLeft={<span className="lx-hide-sm lx-topbar__org">{orgSwitch}<span className="lx-topbar__slash">/</span></span>}
        panelTop={
          <>
            {orgSwitch}
            {planChip}
            <div className="lx-ctx-row">
              {branchSelect}
              {langBtn}
            </div>
          </>
        }
        topRight={
          <>
            <span className="lx-hide-sm">{planChip}</span>
            <span className="lx-hide-sm">{branchSelect}</span>
            <span className="lx-hide-sm">{langBtn}</span>

            <Link
              to="/admin/cart"
              className="lx-iconbtn lx-cart"
              title={tx("ตะกร้า", "Cart")}
              aria-label={tx(`ตะกร้า ${cartItems} รายการ`, `Cart, ${cartItems} items`)}
            >
              <Icon name="cart" />
              {cartItems > 0 ? <span className="lx-cart__count">{cartItems > 99 ? "99+" : cartItems}</span> : null}
            </Link>

            <div className="lx-user">
              <button type="button" className="lx-user__btn" onClick={() => setUserMenu((v) => !v)} aria-label={profile?.fullname || profile?.email}>
                <Avatar name={profile?.fullname || profile?.email} size="sm" />
              </button>
              {userMenu ? (
                <div className="lx-menu">
                  <div className="lx-menu__who">
                    <div className="u-strong">{profile?.fullname || profile?.email}</div>
                    <div className="u-fs-12 u-muted">{profile?.email}</div>
                  </div>
                  <Link to="/admin/organization">{t("nav.settings")}</Link>
                  <button
                    type="button"
                    className="is-danger"
                    onClick={async () => {
                      await logout();
                      navigate("/auth/login");
                    }}
                  >
                    {t("action.logout")}
                  </button>
                </div>
              ) : null}
            </div>
          </>
        }
      >
        <Outlet />
      </Shell>
    </>
  );
}
